// ManiDineRestaurantPortal /src/components/ManagerShell.jsx — Updated 2026-03-29 17:00 (America/Edmonton)
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import DashboardNav from "./DashboardNav";
import { auth, db } from "../firebase";
import IncomingOrderModal from "./IncomingOrderModal";
import { updateOrderStatus } from "../utils/orders";
import useRestaurantContext from "../hooks/useRestaurantContext";

const INCOMING_ORDER_STATUSES = ["new", "order_placed", "pending"];

const ManagerShell = ({ title = "ManiDine Manager", subtitle, children }) => {
  const navigate = useNavigate();
  const { restaurantId, loading: restaurantLoading } =
    useRestaurantContext() || {};

  const [currentIncomingOrder, setCurrentIncomingOrder] = useState(null);
  const [listenerError, setListenerError] = useState(null);

  const audioRef = useRef(null);
  const seenOrderIdsRef = useRef(new Set());
  const initialSnapshotHandledRef = useRef(false);
  const incomingUnsubRef = useRef(null);
  const markSeenTimeoutRef = useRef(null);
  const queueRef = useRef([]);

  // ---- ✅ NEW: Global "typing shield" to stop hotkeys breaking inputs -------
  useEffect(() => {
    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      // contenteditable
      if (el.isContentEditable) return true;
      // some UI libs wrap inputs; handle closest()
      if (typeof el.closest === "function") {
        const closestTyping = el.closest("input, textarea, select, [contenteditable='true']");
        if (closestTyping) return true;
      }
      return false;
    };

    const handleKeyDownCapture = (e) => {
      // If user is typing, block global key handlers (hotkeys)
      if (isTypingTarget(e.target) || isTypingTarget(document.activeElement)) {
        // stop other listeners
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === "function") {
          e.stopImmediatePropagation();
        }
        // do NOT preventDefault (we still want the character to type)
      }
    };

    // Capture phase so we intercept before bubble listeners.
    window.addEventListener("keydown", handleKeyDownCapture, true);

    return () => {
      window.removeEventListener("keydown", handleKeyDownCapture, true);
    };
  }, []);

  // ---- Helpers -------------------------------------------------------------

  const playNewOrderSound = () => {
    const audioEl = audioRef.current;

    if (!audioEl) {
      console.warn("[ManagerShell] new-order audio element not found");
      return;
    }

    // Make sure it's allowed to play
    audioEl.muted = false;
    audioEl.volume = 1;

    try {
      const playPromise = audioEl.play();

      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch((err) => {
          console.warn("[ManagerShell] Audio play was blocked or failed:", err);
        });
      }
    } catch (err) {
      console.warn("[ManagerShell] Audio play threw an error:", err);
    }
  };

  const advanceQueue = () => {
    const nextOrder = queueRef.current.shift() || null;
    setCurrentIncomingOrder(nextOrder || null);
  };

  const getDateFromData = (data) =>
    typeof data.createdAt?.toDate === "function"
      ? data.createdAt.toDate()
      : data.createdAt
      ? new Date(data.createdAt)
      : new Date(0);

  const isIncomingStatus = (status) =>
    INCOMING_ORDER_STATUSES.includes(String(status || "").toLowerCase());

  const enqueueIncomingOrders = (orders, { playSound = false } = {}) => {
    if (!Array.isArray(orders) || !orders.length) return;

    if (playSound) {
      playNewOrderSound();
    }

    setCurrentIncomingOrder((existing) => {
      if (existing) {
        queueRef.current.push(...orders);
        return existing;
      }

      const [firstOrder, ...restOrders] = orders;
      if (restOrders.length) {
        queueRef.current.push(...restOrders);
      }
      return firstOrder;
    });
  };

  // ---- Reset when restaurant changes --------------------------------------

  useEffect(() => {
    if (restaurantLoading) return;
    console.log(
      "[ManagerShell] Resetting incoming-order state for restaurant",
      restaurantId
    );
    seenOrderIdsRef.current = new Set();
    initialSnapshotHandledRef.current = false;
    queueRef.current = [];
    setCurrentIncomingOrder(null);
    setListenerError(null);
  }, [restaurantId, restaurantLoading]);

  // ---- Firestore listener for incoming orders -----------------------------

  useEffect(() => {
    if (restaurantLoading) {
      return () => {};
    }
    if (!restaurantId) {
      console.log("[ManagerShell] No restaurantId, skipping listener");
      return;
    }

    // ✅ React 18 StrictMode + tab switching can mount/unmount quickly.
    // Always cleanup any previous listener before creating a new one.
    if (markSeenTimeoutRef.current) {
      clearTimeout(markSeenTimeoutRef.current);
      markSeenTimeoutRef.current = null;
    }
    if (incomingUnsubRef.current) {
      try {
        incomingUnsubRef.current();
      } catch (_) {}
      incomingUnsubRef.current = null;
    }

    let isActive = true;

    const ordersRef = collection(db, "orders");
    const q = query(ordersRef, where("restaurantId", "==", restaurantId));

    console.log(
      "[ManagerShell] Subscribing to incoming orders for restaurant",
      restaurantId
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (!isActive) return;
        try {
          setListenerError(null);
          console.log(
            "[ManagerShell] Orders snapshot size:",
            snapshot.size,
            "| docChanges:",
            snapshot.docChanges().length
          );

          const docsSortedByCreatedAtDesc = [...snapshot.docs].sort((a, b) => {
            const aData = a.data();
            const bData = b.data();
            return getDateFromData(bData) - getDateFromData(aData);
          });

          // First snapshot = surface any currently waiting incoming orders instead of
          // blindly marking everything as seen.
          if (!initialSnapshotHandledRef.current) {
            const existingIncomingOrders = docsSortedByCreatedAtDesc
              .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
              .filter((data) => isIncomingStatus(data.status));

            console.log(
              "[ManagerShell] First snapshot incoming orders:",
              existingIncomingOrders.map((order) => ({
                id: order.id,
                status: order.status,
              }))
            );

            docsSortedByCreatedAtDesc.forEach((docSnap) => {
              seenOrderIdsRef.current.add(docSnap.id);
            });

            if (existingIncomingOrders.length) {
              enqueueIncomingOrders(existingIncomingOrders, { playSound: true });
            }

            initialSnapshotHandledRef.current = true;
            return;
          }

          // After first snapshot, handle only brand-new docs
          const changesSortedByCreatedAtDesc = [...snapshot.docChanges()].sort(
            (a, b) =>
              getDateFromData(b.doc.data()) - getDateFromData(a.doc.data())
          );

          changesSortedByCreatedAtDesc.forEach((change) => {
            const id = change.doc.id;
            const data = { id, ...change.doc.data() };
            const status = (data.status || "").toLowerCase();

            console.log(
              "[ManagerShell] docChange:",
              change.type,
              "id=",
              id,
              "status=",
              status
            );

            if (change.type !== "added") return;

            if (seenOrderIdsRef.current.has(id)) {
              console.log("[ManagerShell]   → already seen, skipping");
              return;
            }
            seenOrderIdsRef.current.add(id);

            if (!isIncomingStatus(status)) {
              console.log(
                "[ManagerShell]   → status not incoming, skipping popup"
              );
              return;
            }

            console.log(
              "[ManagerShell]   → NEW incoming order, showing popup + sound"
            );

            // New order! Play sound + show modal (or queue if one already open)
            enqueueIncomingOrders([data], { playSound: true });
          });
        } catch (e) {
          console.error("[ManagerShell] Incoming orders snapshot handling error:", e);
        }
      },
      (err) => {
        console.error("[ManagerShell] Incoming orders listener error:", err);
        setListenerError(err);
      }
    );

    incomingUnsubRef.current = unsubscribe;

    return () => {
      isActive = false;
      if (markSeenTimeoutRef.current) {
        clearTimeout(markSeenTimeoutRef.current);
        markSeenTimeoutRef.current = null;
      }
      console.log("[ManagerShell] Unsubscribing incoming orders listener");
      try {
        unsubscribe();
      } catch (_) {}
      incomingUnsubRef.current = null;
      seenOrderIdsRef.current = new Set();
      initialSnapshotHandledRef.current = false;
      queueRef.current = [];
    };
  }, [restaurantId, restaurantLoading]);

  // Just log listener errors to console
  useEffect(() => {
    if (listenerError) {
      console.error("Incoming orders listener stopped:", listenerError);
    }
  }, [listenerError]);

  // ---- Auth / Logout ------------------------------------------------------

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login", { replace: true });
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  // ---- Modal handlers ------------------------------------------------------

  const resolveIncomingOrder = () => {
    advanceQueue();
  };

  const handleModalAccept = async (acceptPayload = {}) => {
    if (!restaurantId || !currentIncomingOrder) {
      throw new Error("Incoming order is no longer available.");
    }

    try {
      await updateOrderStatus(
        restaurantId,
        currentIncomingOrder.id,
        "order_confirmed",
        acceptPayload
      );
      resolveIncomingOrder();
    } catch (err) {
      console.error("Failed to accept order:", err);
      throw err;
    }
  };

  const handleModalReject = async () => {
    if (!restaurantId || !currentIncomingOrder) return;
    try {
      await updateOrderStatus(
        restaurantId,
        currentIncomingOrder.id,
        "cancelled"
      );
    } catch (err) {
      console.error("Failed to reject order:", err);
    } finally {
      resolveIncomingOrder();
    }
  };

  const handleModalClose = (meta = {}) => {
    if (!currentIncomingOrder) return;

    if (meta?.forceResolve) {
      resolveIncomingOrder();
      return;
    }

    const currentStatus = String(currentIncomingOrder.status || "").toLowerCase();
    if (isIncomingStatus(currentStatus)) {
      window.alert("This order is still pending. Please Accept or Reject it to continue.");
      return;
    }

    resolveIncomingOrder();
  };

  // ---- Render -------------------------------------------------------------

  return (
    <div className="dashboard-layout">
      {/* Global audio element for new-order sound */}
      <audio
        id="new-order-audio"
        ref={audioRef}
        src="/sounds/new-order.mp3"
        preload="auto"
      />

      <DashboardNav />

      <div className="dashboard-main-area">
        <header className="topbar">
          <div>
            <p className="topbar-greeting">{title}</p>
            {subtitle && <p className="topbar-subtext">{subtitle}</p>}
          </div>

          <div className="topbar-right">
            {auth.currentUser?.email && (
              <span className="topbar-email">{auth.currentUser.email}</span>
            )}
            <button
              type="button"
              className="topbar-help"
              onClick={() => navigate("/support")}
            >
              Help
            </button>
            <button
              type="button"
              className="topbar-logout"
              onClick={handleLogout}
            >
              Log out
            </button>
          </div>
        </header>

        <div className="dashboard-content-area">{children}</div>
      </div>

      {/* Incoming order popup, available on ANY page */}
      {currentIncomingOrder && (
        <IncomingOrderModal
          order={currentIncomingOrder}
          onAccept={handleModalAccept}
          onReject={handleModalReject}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default ManagerShell;
